import { events } from "constants/tagManager";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import dayjs from "dayjs";
import { useAnalytics } from "hooks/useAnalytics";
import useFormValidation from "hooks/useFormValidation";
import { useOrderReadiness } from "pages/CreateOrder/hooks/useOrderReadiness";
import {
  validateScheduledFirstCommunicationDate,
  mapFormToRequestBody,
} from "pages/CreateOrder/utils/helpers";
import { Button } from "primereact/button";
import { CalendarValueType } from "primereact/calendar";
import { useContext, useMemo, useEffect, useState } from "react";
import { useFormContext, useFormState, Controller } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "store";
import {
  useApproveDraftOrderMutation,
  useCreateOrdersMutation,
  useRedoOrderMutation,
} from "store/queries/order";
import { cleanUpServiceDetails } from "store/slices/serviceDetails";
import styled from "styled-components";
import { TourAnchor } from "training/anchors";
import {
  isTrainingTourOnCreatePage,
  notifyTrainingOrderCreated,
} from "training/engine/session";

import { ScheduleDateTimeDialog } from "./components/ScheduleDateTimeDialog";

const StyledDeleteButton = styled(Button)`
  padding: 0 !important;
`;

export function Submit({
  draftOrder,
  redo = false,
}: {
  draftOrder?: boolean;
  redo?: boolean;
}) {
  const dispatch = useDispatch();
  const [useCreateOrders] = useCreateOrdersMutation();
  const [useApproveDraft] = useApproveDraftOrderMutation();
  const [redoOrder] = useRedoOrderMutation();
  const { setIsLoading } = useLoading();
  const [showScheduleDateTimeDialog, setShowScheduleDateTimeDialog] =
    useState(false);

  const { loadingPriceBreakdown } = useSelector(
    (state: RootState) => state.serviceDetailsSlice
  );
  const { pageViewEvents } = useAnalytics();

  const { current: toastElement } = useContext(ToastContext);
  const {
    getValues,
    watch,
    trigger,
    formState: { errors },
    control,
    setValue,
  } = useFormContext();
  const { isValid } = useFormState();
  const navigate = useNavigate();
  const { getFormErrorMessage } = useFormValidation();

  const procedures = watch("serviceDetails");
  const isEditingMessage = watch("isEditingMessage");
  const dateOfService = watch("dateOfService");
  const scheduledFirstCommunicationSent = watch(
    "scheduledFirstCommunicationSent"
  );
  const scheduledFirstCommunicationDate = watch(
    "scheduledFirstCommunicationDate"
  );
  const [selectedDate, setSelectedDate] = useState<CalendarValueType>(
    scheduledFirstCommunicationDate ?? dayjs().add(2, "minute").toDate()
  );

  useEffect(() => {
    validateScheduledFirstCommunicationDate(
      scheduledFirstCommunicationDate,
      dateOfService,
      scheduledFirstCommunicationSent
    );
    trigger("scheduledFirstCommunicationDate");
  }, [
    scheduledFirstCommunicationDate,
    dateOfService,
    scheduledFirstCommunicationSent,
  ]);

  // An open SMS editor means the message is mid-edit — creating, saving or
  // scheduling now would send whatever half-typed text is in the box.
  const isButtonDisabled = !useOrderReadiness();
  // CLE-2971 — a pending eligibility check disables Create and Schedule, never Save Draft.
  const isSaveDraftDisabled = !useOrderReadiness({
    allowPendingEligibility: true,
  });

  const showCalendar = useMemo(() => {
    if (redo && !scheduledFirstCommunicationDate) {
      return false;
    }
    return true;
  }, [scheduledFirstCommunicationSent]);

  const viewCreatedOrder = (id: string) => {
    navigate(`/orders/${id}`);
  };

  const createOrder = async (saveAsDraft = false) => {
    const isValid = await trigger("scheduledFirstCommunicationDate");
    if (!isValid) {
      return;
    }
    setIsLoading(true);
    // CLE-3015 — a Clear Certified trainee's own practice estimate: the API tags its history.
    const training = isTrainingTourOnCreatePage();
    const requestBody = {
      ...mapFormToRequestBody(getValues(), saveAsDraft),
      ...(training ? { training: true } : {}),
    };
    useCreateOrders(requestBody)
      .unwrap()
      .then((createdOrder) => {
        pageViewEvents(
          {
            accountName: createdOrder.data.account.name,
            orderType: createdOrder.data.orderType,
            communicationMethod:
              createdOrder.data.patient.preferredContactMethod,
          },
          events.ORDER_CREATED
        );

        setIsLoading(false);
        dispatch(cleanUpServiceDetails());

        if (training) {
          // Before navigating: the tour moves on (and hands over to the hospital view) on this event.
          notifyTrainingOrderCreated({
            id: createdOrder.data.id,
            externalId: createdOrder.data.externalId,
            status: createdOrder.data.status,
            orderType: createdOrder.data.orderType,
            planEnabled: !!createdOrder.data.paymentPlan?.length,
          });
        }

        toastElement?.show({
          severity: "success",
          summary: "Estimate created",
          detail: "Estimate created successfully",
          content: (
            <div className="card flex flex-wrap justify-content-center gap-3 text-center">
              <h3>
                {saveAsDraft ? "Draft Estimate Created" : "Estimate Created!"}
              </h3>
              <p>
                {saveAsDraft
                  ? "Draft estimate has been successfully created"
                  : `Your estimate has been successfully created! The patient has been
                sent communication for payment collection.`}
              </p>
              <p className="w-full">
                {saveAsDraft
                  ? "Select to view the estimate"
                  : "To begin collecting payment, select to view the estimate."}
              </p>
              <Button
                label="View Estimate"
                onClick={() => viewCreatedOrder(createdOrder.data.id)}
              />
            </div>
          ),
        });
        navigate(`/hospitals/view/${createdOrder.data.account.id}/orders`);
      })
      .catch(() => {
        setIsLoading(false);

        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: "An error has occurred attempting to create the Estimate",
        });
      });
  };

  const updateOrder = async () => {
    const isValid = await trigger("scheduledFirstCommunicationDate");
    if (!isValid) {
      return;
    }
    setIsLoading(true);
    const requestBody = mapFormToRequestBody(getValues());

    useApproveDraft(requestBody)
      .unwrap()
      .then((createdOrder) => {
        pageViewEvents(
          {
            accountName: createdOrder.data.account.name,
            orderType: createdOrder.data.orderType,
            communicationMethod:
              createdOrder.data.patient.preferredContactMethod,
          },
          events.ORDER_CREATED
        );

        setIsLoading(false);
        dispatch(cleanUpServiceDetails());

        toastElement?.show({
          severity: "success",
          summary: "Estimate approved",
          detail: "Estimate approved successfully",
        });
        navigate(`/hospitals/view/${createdOrder.data.account.id}/orders`);
      })
      .catch(() => {
        setIsLoading(false);

        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: "An error has occurred attempting to create the Estimate",
        });
      });
  };

  const handleRedoOrder = async (saveAsDraft = false) => {
    const isValid = await trigger("scheduledFirstCommunicationDate");
    if (!isValid) {
      return;
    }
    if (
      saveAsDraft &&
      !scheduledFirstCommunicationSent &&
      scheduledFirstCommunicationDate
    ) {
      setValue("scheduledFirstCommunicationDate", null);
      setValue("scheduledFirstCommunicationSent", false);
    }
    setIsLoading(true);
    const requestBody = { ...mapFormToRequestBody(getValues()), saveAsDraft };
    redoOrder(requestBody)
      .unwrap()
      .then((redoneOrder) => {
        pageViewEvents(
          {
            accountName: redoneOrder.data.account.name,
            orderType: redoneOrder.data.orderType,
            communicationMethod:
              redoneOrder.data.patient.preferredContactMethod,
          },
          events.ORDER_REDONE
        );

        setIsLoading(false);
        dispatch(cleanUpServiceDetails());

        toastElement?.show({
          severity: "success",
          summary: "Estimate redone",
          detail: "Estimate redone successfully",
          content: (
            <div className="card flex flex-wrap justify-content-center gap-3 text-center">
              <h3>
                {saveAsDraft ? "Estimate Status Changed" : "Estimate Redone!"}
              </h3>
              <p>
                {saveAsDraft
                  ? `Your estimate has been successfully saved as draft`
                  : `Your estimate has been successfully redone! The patient has been
                sent updated communication for payment collection.`}
              </p>
              <p>
                {saveAsDraft
                  ? "Select to view the estimate"
                  : "To begin collecting payment, select to view the estimate."}
              </p>
              <Button
                label="View Estimate"
                onClick={() => viewCreatedOrder(redoneOrder.data.id)}
              />
            </div>
          ),
        });
        navigate(`/hospitals/view/${redoneOrder.data.account.id}/orders`);
      })
      .catch(() => {
        setIsLoading(false);

        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: "An error has occurred attempting to redo the Estimate",
        });
      });
  };

  const handleButtonClick = async () => {
    const isValid = await trigger("scheduledFirstCommunicationDate");
    if (!isValid) {
      return;
    }
    if (draftOrder) {
      updateOrder();
      return;
    }
    if (redo) {
      handleRedoOrder();
      return;
    }
    createOrder();
  };

  const createButtonText = useMemo(() => {
    if (redo) {
      return "Save Changes";
    }
    return scheduledFirstCommunicationDate
      ? "Create and Save"
      : "Create and Send";
  }, [redo, scheduledFirstCommunicationDate]);

  return (
    <div className="flex flex-column" data-tour={TourAnchor.SubmitFooter}>
      <div className="flex flex-column md:flex-row md:justify-content-end gap-2">
        <ScheduleDateTimeDialog
          onClose={() => setShowScheduleDateTimeDialog(false)}
          visible={showScheduleDateTimeDialog}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onSave={handleButtonClick}
        />
        {!draftOrder && (
          <div
            data-testid="saveDraft_ReviewOrder"
            className="flex justify-content-center"
          >
            <Button
              className="p-button-action p-button-neutral"
              icon="pi pi-save"
              label="Save Draft"
              onClick={() => (redo ? handleRedoOrder(true) : createOrder(true))}
              loading={loadingPriceBreakdown}
              disabled={isSaveDraftDisabled}
            />
          </div>
        )}
        <div className="flex justify-content-center md:justify-content-end align-items-start gap-2">
          {showCalendar && (
            <div className="flex flex-column align-items-end">
              <div className="flex">
                <Button
                  className="p-button-action p-button-neutral white-space-nowrap"
                  disabled={scheduledFirstCommunicationSent || isButtonDisabled}
                  icon="pi pi-calendar"
                  onClick={() => setShowScheduleDateTimeDialog(true)}
                >
                  {!scheduledFirstCommunicationDate && "Schedule send"}
                  {scheduledFirstCommunicationDate && (
                    <span>
                      Scheduled
                      <span className="ml-1 text-sm">
                        (
                        {dayjs(scheduledFirstCommunicationDate).format(
                          "MM/DD/YYYY hh:mm A"
                        )}
                        )
                      </span>
                    </span>
                  )}
                </Button>
                {scheduledFirstCommunicationDate && (
                  <StyledDeleteButton
                    className="p-button-text p-button-danger max-w-12rem"
                    onClick={() => {
                      setValue("scheduledFirstCommunicationDate", null);
                      setSelectedDate(dayjs().add(2, "minute").toDate());
                    }}
                    disabled={scheduledFirstCommunicationSent}
                    icon="pi pi-trash"
                  />
                )}
              </div>
              {getFormErrorMessage("scheduledFirstCommunicationDate", errors)}
              <Controller
                data-testid="scheduledFirstCommunicationDate"
                name="scheduledFirstCommunicationDate"
                control={control}
                shouldUnregister={false}
                rules={{
                  validate: (value) =>
                    validateScheduledFirstCommunicationDate(
                      value,
                      dateOfService,
                      scheduledFirstCommunicationSent
                    ),
                }}
                // No visible UI here — this just keeps the field alive
                render={() => <div className="hidden">test</div>}
              />
            </div>
          )}

          <div data-testid="submit" data-tour={TourAnchor.CreateAndSend}>
            <Button
              className="p-button-action"
              icon="pi pi-send"
              label={createButtonText}
              onClick={handleButtonClick}
              disabled={isButtonDisabled}
              loading={loadingPriceBreakdown}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
